import { c } from '../../utils/tailwind-merge';
import { RichText } from '../RichText/RichText';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableRow,
} from '../Table/Table';
import {
  ProductCompatibilityTableMultiPackagePropsDataWithMicrocopy,
  ProductCompatibilityTableProps,
  ProductCompatibilityTableSinglePackagePropsDataWithMicrocopy,
} from './types';

const ProductCompatibilitySinglePackage = ({
  unityVersion,
  renderPipelines,
  buildTarget,
  fileSize,
  latestVersion,
  latestReleaseDate,
  microcopy,
  className = '',
}: ProductCompatibilityTableSinglePackagePropsDataWithMicrocopy & {
  className?: string;
}) => {
  const formatValue = (value: string | string[]) =>
    Array.isArray(value) ? value.join(', ') : value;

  const rows = [
    [microcopy.unityVersion, unityVersion],
    [microcopy.renderPipelines, renderPipelines],
    [microcopy.buildTarget, buildTarget],
    [microcopy.fileSize, fileSize],
    [microcopy.latestVersion, latestVersion],
    [microcopy.latestRelease, latestReleaseDate],
  ].filter(([label, value]) => label && value) as Array<
    [string, string | string[]]
  >;

  const LABEL_LENGTH_BREAK_THRESHOLD = 10;

  return (
    <Table className={className}>
      <TableCaption>{microcopy?.title}</TableCaption>
      <TableBody>
        {rows.map(([label, value]) => (
          <TableRow key={label}>
            <TableCell>{label}</TableCell>
            <TableCell
              className={c(
                'w-40',
                label.length > LABEL_LENGTH_BREAK_THRESHOLD
                  ? 'break-normal'
                  : 'break-all'
              )}
            >
              {formatValue(value)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const ProductCompatibilityMultiPackages = ({
  microcopy,
  className = '',
}: ProductCompatibilityTableMultiPackagePropsDataWithMicrocopy & {
  className?: string;
}) => {
  return (
    <div className={c('relative w-full max-w-[385px]', className)}>
      <p className="body-small-medium text-muted-foreground mb-4 break-all text-left">
        {microcopy.title}
      </p>
      <RichText
        className={c(
          'tiny-text',
          '[&_a]:tiny-text-link [&_a]:text-blue-hover [&_a]:no-underline ',
          '[&_a:visited]:tiny-text-link'
        )}
        html={microcopy.description}
      />
    </div>
  );
};

export const ProductCompatibilityTable = ({
  microcopy,
  ...props
}: ProductCompatibilityTableProps) => {
  if (props.variant === 'single-package') {
    return (
      <ProductCompatibilitySinglePackage {...props} microcopy={microcopy} />
    );
  }

  return <ProductCompatibilityMultiPackages {...props} microcopy={microcopy} />;
};
